/**
 * Modelo de documento da impressão (Administração › Geral, item 25): toda impressão (painel, projeto, relatórios, CX)
 * sai no padrão visual da AD Digital, o mesmo das propostas do Precificador (modelo "planilha"):
 * - capa: a foto da marca no alto e o bloco preto com o título (ciano), o assunto e a data por extenso;
 * - cada página: o cabeçalho (logo e a faixa azul e roxa) no alto e o rodapé (faixa cinza com addigital.com.br) no pé.
 * Com o modelo do Word ou imagens enviadas na Administração, o cabeçalho e o rodapé são os enviados.
 * No papel: cabeçalho e rodapé fixos (o navegador repete elementos fixos em toda página) e uma tabela cujo cabeçalho e
 * rodapé (que também se repetem) guardam o espaço deles. Na tela, nada disso aparece (index.css, .print-frame).
 */
import { useEffect, useState, type ReactNode } from 'react';
import { flushSync } from 'react-dom';
import { useAuth } from '../lib/auth';
import { branding } from '../lib/branding';
import { fmtDateTime } from '../lib/format';
import { useCurrentPrintCover } from '../lib/printCover';

const AD = { cover: '/brand/impressao/capa.jpg', header: '/brand/impressao/cabecalho.png', footer: '/brand/impressao/rodape.png' };

function Head() {
  const p = branding().print;
  return p.header ? <img src={p.header} alt="" className="pf-img" /> : <div className="pf-ad-head" aria-hidden="true" />;
}

function Foot({ printedBy }: { printedBy: string | null }) {
  const p = branding().print;
  // No modelo AD, o rodapé já traz addigital.com.br: o texto "AD Digital" padrão não se repete.
  const text = p.footer || p.footerText !== 'AD Digital' ? p.footerText : '';
  return (
    <>
      {text || printedBy ? <div className="pf-text"><span>{text}</span>{printedBy ? <span>{printedBy}</span> : null}</div> : null}
      {p.footer ? <img src={p.footer} alt="" className="pf-img" /> : <div className="pf-ad-foot" aria-hidden="true" />}
    </>
  );
}

const longDate = () => new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Sao_Paulo', dateStyle: 'full' }).format(new Date());

function Cover({ title, subtitle }: { title: string; subtitle?: string | null }) {
  const cover = branding().print.cover ?? AD.cover;
  return (
    <div className="pf-cover" aria-hidden="true">
      <div className="pf-cover-photo" style={{ backgroundImage: `url("${cover}")` }} />
      <div className="pf-cover-block" />
      <div className="pf-cover-text">
        <p className="pf-cover-title">{title}</p>
        {subtitle ? <p className="pf-cover-subtitle">{subtitle}</p> : null}
        <p className="pf-cover-date">{longDate()}</p>
      </div>
    </div>
  );
}

export function PrintFrame({ children }: { children: ReactNode }) {
  const { me } = useAuth();
  const p = branding().print;
  const cover = useCurrentPrintCover();
  // Sem capa definida pela tela: o título da tela, lido na hora de imprimir.
  const [fallback, setFallback] = useState<string>('');
  useEffect(() => {
    const before = () => flushSync(() => setFallback(document.querySelector('main h1')?.textContent?.trim() || 'PMIS ADD'));
    window.addEventListener('beforeprint', before);
    // As imagens do modelo só aparecem no papel: baixadas antes, com a tela parada, já estão prontas no primeiro Ctrl+P.
    const t = window.setTimeout(() => {
      for (const src of [p.header ?? AD.header, p.footer ?? AD.footer, p.showCover !== false ? p.cover ?? AD.cover : null]) if (src) new Image().src = src;
    }, 3000);
    return () => { window.removeEventListener('beforeprint', before); window.clearTimeout(t); };
  }, [p]);
  const printedBy = p.showPrintedBy ? `Impresso em ${fmtDateTime(new Date().toISOString())}${me ? ` por ${me.displayName}` : ''}` : null;
  const custom = !!(p.header || p.footer);
  return (
    <>
      {p.showCover !== false ? <Cover title={cover?.title ?? fallback} subtitle={cover?.subtitle} /> : null}
      <table className={custom ? 'print-frame' : 'print-frame pf-ad'}>
        <thead><tr><td><div className="pf-space" aria-hidden="true"><Head /></div></td></tr></thead>
        <tbody><tr><td>{children}</td></tr></tbody>
        <tfoot><tr><td><div className="pf-space" aria-hidden="true"><Foot printedBy={printedBy} /></div></td></tr></tfoot>
      </table>
      <div className="pf-fixed pf-fixed-head"><Head /></div>
      <div className="pf-fixed pf-fixed-foot"><Foot printedBy={printedBy} /></div>
    </>
  );
}

export const AD_PRINT_IMAGES = AD;
